import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

import { discussionCsApi, DiscussionCsApiError } from './chapterDiscussions.api'
import { canUnblockDiscussionUser } from './chapterDiscussions.model'
import { DiscussionActionDialog } from './DiscussionActionDialog'
import { DiscussionBulkDeleteJobPanel } from './DiscussionBulkDeleteJobPanel'

import type {
  DiscussionUserBulkDeleteJob,
  DiscussionUserEnforcement,
} from './chapterDiscussions.types'

export interface DiscussionUserEnforcementPanelProps {
  userId: number
  enforcement: DiscussionUserEnforcement | null
}

type EnforcementAction = 'block' | 'block_and_delete_all' | 'unblock'

export function DiscussionUserEnforcementPanel({
  userId,
  enforcement: initialEnforcement,
}: DiscussionUserEnforcementPanelProps) {
  const [enforcement, setEnforcement] = useState(initialEnforcement)
  const [currentJob, setCurrentJob] =
    useState<DiscussionUserBulkDeleteJob | null>(null)
  const [action, setAction] = useState<EnforcementAction | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setEnforcement(initialEnforcement), [initialEnforcement])

  useEffect(() => {
    let active = true
    const jobId = enforcement?.currentBulkDeleteJobId
    if (!jobId) {
      setCurrentJob(null)
      return
    }
    discussionCsApi
      .getBulkDeleteJob(jobId)
      .then(response => {
        if (active) setCurrentJob(response.job)
      })
      .catch(requestError => {
        if (active) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'Unable to load delete-all state',
          )
        }
      })
    return () => {
      active = false
    }
  }, [enforcement?.currentBulkDeleteJobId])

  const submit = async (reason: string) => {
    if (!action) return
    setPending(true)
    setError(null)
    try {
      const response =
        action === 'unblock'
          ? await discussionCsApi.unblockUser(userId, {
              reason,
              expectedGeneration: enforcement?.generation ?? 0,
            })
          : await discussionCsApi.blockUser(userId, { action, reason })
      setEnforcement(response.record)
      setAction(null)
      window.dispatchEvent(new Event('discussion-detail-refresh'))
    } catch (requestError) {
      const apiError = requestError as DiscussionCsApiError
      setError(apiError.message)
      if (apiError.status === 409) {
        setAction(null)
        window.dispatchEvent(new Event('discussion-detail-refresh'))
      }
    } finally {
      setPending(false)
    }
  }

  const blocked = enforcement?.status === 'blocked'
  const unblockEnabled = canUnblockDiscussionUser(enforcement, currentJob)
  const description =
    action === 'block_and_delete_all'
      ? `Block user ${userId} and resumably delete every discussion comment by this user.`
      : action === 'block'
        ? `Block user ${userId} from future discussion writes. Existing public content remains.`
        : `Unblock user ${userId}. Previously deleted content will not be restored.`

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}>
        <Box>
          <Typography variant="h6">User enforcement</Typography>
          <Typography variant="body2" color="text.secondary">
            User {userId} · generation {enforcement?.generation ?? 0}
          </Typography>
        </Box>
        <Chip
          color={blocked ? 'error' : 'default'}
          label={blocked ? 'blocked' : 'not blocked'}
        />
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <ButtonGroup sx={{ mt: 2 }} variant="outlined">
        {!blocked && (
          <Button color="warning" onClick={() => setAction('block')}>
            Block user
          </Button>
        )}
        {!blocked && (
          <Button
            color="error"
            onClick={() => setAction('block_and_delete_all')}>
            Block and delete all
          </Button>
        )}
        {blocked && (
          <Button
            disabled={!unblockEnabled}
            onClick={() => setAction('unblock')}>
            Unblock user
          </Button>
        )}
      </ButtonGroup>
      {blocked && enforcement?.currentBulkDeleteJobId && !unblockEnabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Unblock remains disabled until the current delete-all generation is
          complete.
        </Alert>
      )}
      {enforcement?.currentBulkDeleteJobId && (
        <Box sx={{ mt: 2 }}>
          <DiscussionBulkDeleteJobPanel
            jobId={enforcement.currentBulkDeleteJobId}
          />
        </Box>
      )}
      <DiscussionActionDialog
        open={action !== null}
        title={
          action === 'block_and_delete_all'
            ? 'Block and delete all comments'
            : action === 'block'
              ? 'Block discussion user'
              : 'Unblock discussion user'
        }
        description={description}
        confirmLabel={
          action === 'unblock'
            ? 'Unblock'
            : action === 'block'
              ? 'Block'
              : 'Block and delete all'
        }
        destructive={action === 'block_and_delete_all'}
        pending={pending}
        error={error}
        onClose={() => setAction(null)}
        onConfirm={submit}
      />
    </Paper>
  )
}
